import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Reusable Back Button Component
 * Uses browser history to go back, or navigates to fallback path.
 *
 * Note: we do NOT register a custom listener for the mouse back button (button 3 / 4).
 * The browser already dispatches a native back-navigation for that button which
 * React Router picks up via popstate. Adding a JS handler caused a double-back
 * (JS navigate(-1) + native back) that often ended at /dashboard or earlier.
 *
 * @param {string} fallbackPath - Path to navigate if no history (optional)
 * @param {string} label - Button label (default: common.back)
 * @param {string} className - Additional CSS classes
 */
export const BackButton = ({
  fallbackPath = '/dashboard',
  label,
  className = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  return (
    <button
      onClick={handleBack}
      className={`inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-surface-50 font-medium transition ${className}`}
    >
      <svg 
        className="w-5 h-5 rtl:-scale-x-100" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M10 19l-7-7m0 0l7-7m-7 7h18" 
        />
      </svg>
      <span>{label ?? t('common.back')}</span>
    </button>
  );
};

export default BackButton;
